import React, { useEffect, useRef } from 'react';

import { useAssistant } from './AssistantProvider';
import EmptyState from './EmptyState';
import UserBubble from './messages/UserBubble';
import AssistantMessage from './messages/AssistantMessage';
import SystemMessage from './messages/SystemMessage';

/* Scrollable transcript. Auto-sticks to the bottom as content streams in. */
const Transcript = () => {
  const { messages, turnActive, conversationId } = useAssistant();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Show a standalone typing indicator the moment a turn is active but the
  // assistant message hasn't been created yet (last message is still the user's).
  const lastMessage = messages[messages.length - 1];
  const showPendingTyping =
    turnActive && (!lastMessage || lastMessage.role === 'USER');

  // Derive a signal that changes on every streamed tick so we re-stick to bottom.
  const contentSignal =
    messages
      .map(
        (m) =>
          `${m.external_id || m.client_message_id}:${m.content.length}:${
            m.tool_calls.length
          }`
      )
      .join('|') + `|pending:${showPendingTyping}`;

  // Stick to the bottom while streaming, but only if the reader hasn't
  // scrolled up to read an earlier message - otherwise each streamed update
  // would yank them back down.
  const NEAR_BOTTOM_PX = 80;
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
    if (nearBottom) el.scrollTop = el.scrollHeight;
  }, [contentSignal]);

  // Always jump to the bottom when switching to a different conversation.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [conversationId]);

  return (
    <div className="bra-scroll" ref={scrollRef}>
      {messages.length === 0 && !showPendingTyping ? (
        <EmptyState />
      ) : (
        <>
          {messages.map((m) => {
            const key = m.external_id || m.client_message_id;
            if (m.isActionDecisionFollowUp) return null;
            if (m.role === 'USER') return <UserBubble key={key} message={m} />;
            if (m.role === 'SYSTEM')
              return <SystemMessage key={key} message={m} />;
            return <AssistantMessage key={key} message={m} />;
          })}
          {showPendingTyping ? (
            <div className="bra-msg bra-a">
              <div className="bra-body">
                <span className="bra-typing">
                  <i />
                  <i />
                  <i />
                </span>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default Transcript;
